// Sons synthétisés en WebAudio — zéro asset, zéro chargement.
// L'AudioContext ne démarre qu'au premier geste utilisateur (règle navigateur).

const KEY_MUTE = 'cryptoket.mute'

let ctx: AudioContext | null = null

function audio(): AudioContext | null {
  if (isMuted()) return null
  if (!ctx) {
    try {
      ctx = new AudioContext()
    } catch {
      return null
    }
  }
  if (ctx.state === 'suspended') void ctx.resume()
  return ctx
}

export function isMuted(): boolean {
  return localStorage.getItem(KEY_MUTE) === '1'
}

export function toggleMute(): boolean {
  const muted = !isMuted()
  localStorage.setItem(KEY_MUTE, muted ? '1' : '0')
  return muted
}

interface Tone {
  freq: number
  end?: number // fréquence de fin (glissando)
  dur: number
  type?: OscillatorType
  gain?: number
  at?: number // décalage en secondes
}

function play(tones: Tone[]) {
  const ac = audio()
  if (!ac) return
  const now = ac.currentTime
  for (const t of tones) {
    const osc = ac.createOscillator()
    const g = ac.createGain()
    const start = now + (t.at ?? 0)
    osc.type = t.type ?? 'square'
    osc.frequency.setValueAtTime(t.freq, start)
    if (t.end) osc.frequency.exponentialRampToValueAtTime(t.end, start + t.dur)
    g.gain.setValueAtTime(t.gain ?? 0.08, start)
    g.gain.exponentialRampToValueAtTime(0.0001, start + t.dur)
    osc.connect(g).connect(ac.destination)
    osc.start(start)
    osc.stop(start + t.dur + 0.02)
  }
}

export const sfx = {
  hit: () => play([{ freq: 160, end: 70, dur: 0.12, type: 'square', gain: 0.1 }]),
  crit: () =>
    play([
      { freq: 200, end: 60, dur: 0.18, type: 'sawtooth', gain: 0.12 },
      { freq: 1200, end: 300, dur: 0.15, type: 'square', gain: 0.05, at: 0.02 },
    ]),
  special: () => play([{ freq: 300, end: 900, dur: 0.2, type: 'sawtooth', gain: 0.07 }]),
  defend: () => play([{ freq: 500, end: 350, dur: 0.15, type: 'triangle', gain: 0.07 }]),
  heal: () =>
    play([
      { freq: 523, dur: 0.1, type: 'triangle' },
      { freq: 659, dur: 0.12, type: 'triangle', at: 0.08 },
    ]),
  energy: () => play([{ freq: 880, end: 1100, dur: 0.08, type: 'sine', gain: 0.05 }]),
  ko: () => play([{ freq: 300, end: 40, dur: 0.5, type: 'sawtooth', gain: 0.12 }]),
  click: () => play([{ freq: 700, dur: 0.05, type: 'square', gain: 0.04 }]),
  // coquille qui craque : claquement sec + écho aigu
  crack: () =>
    play([
      { freq: 950, end: 180, dur: 0.06, type: 'square', gain: 0.1 },
      { freq: 2400, end: 700, dur: 0.05, type: 'square', gain: 0.04, at: 0.015 },
    ]),
  // éclosion : arpège ascendant + boom grave du burst
  hatch: () =>
    play([
      { freq: 392, dur: 0.1, type: 'triangle' },
      { freq: 523, dur: 0.1, type: 'triangle', at: 0.09 },
      { freq: 659, dur: 0.1, type: 'triangle', at: 0.18 },
      { freq: 1046, dur: 0.26, type: 'triangle', gain: 0.09, at: 0.27 },
      { freq: 130, end: 36, dur: 0.4, type: 'sawtooth', gain: 0.11, at: 0.05 },
    ]),
  victory: () =>
    play([
      { freq: 523, dur: 0.12 },
      { freq: 659, dur: 0.12, at: 0.11 },
      { freq: 784, dur: 0.12, at: 0.22 },
      { freq: 1047, dur: 0.25, at: 0.33 },
    ]),
  defeat: () =>
    play([
      { freq: 392, dur: 0.18, type: 'triangle' },
      { freq: 311, dur: 0.18, type: 'triangle', at: 0.16 },
      { freq: 233, dur: 0.35, type: 'triangle', at: 0.32 },
    ]),
  levelup: () =>
    play([
      { freq: 659, dur: 0.09 },
      { freq: 784, dur: 0.09, at: 0.08 },
      { freq: 988, dur: 0.09, at: 0.16 },
      { freq: 1319, dur: 0.2, at: 0.24 },
    ]),
  evolve: () =>
    play([
      { freq: 262, end: 523, dur: 0.35, type: 'sawtooth', gain: 0.06 },
      { freq: 523, dur: 0.12, type: 'triangle', at: 0.32 },
      { freq: 659, dur: 0.12, type: 'triangle', at: 0.44 },
      { freq: 784, dur: 0.12, type: 'triangle', at: 0.56 },
      { freq: 1047, dur: 0.3, type: 'triangle', at: 0.68 },
      { freq: 2093, end: 1568, dur: 0.4, type: 'sine', gain: 0.04, at: 0.68 },
    ]),
}
